const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface Role {
  id: string;
  name: string;
  description?: string;
  organization_id?: number;
  created_at: string;
  updated_at: string;
}

export interface FeatureFlag {
  id: number;
  roleid: string;
  featurekey: string;
  enabled: boolean;
  createdat: string;
  updatedat: string;
}

export interface FeatureWithAssignment {
  id: number;
  key: string;
  name: string;
  description: string;
  category: string;
  route_path?: string;
  menu_label?: string;
  menu_icon?: string;
  menu_order?: number;
  is_menu_item: boolean;
  is_page: boolean;
  enabled: boolean;
  assigned: boolean;
  assignment_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateRoleRequest {
  id: string;
  name: string;
  description?: string;
}

export interface UpdateRoleRequest {
  name: string;
  description?: string;
}

export interface UpdateFeatureFlagRequest {
  featurekey: string;
  enabled: boolean;
}

export const RoleManagementService = {
  // Get all feature roles for the user's organization
  getAllRoles: async (): Promise<Role[]> => {
    try {
      const response = await fetch(`${API_URL}/roles/feature-roles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      throw error;
    }
  },

  // Create a new feature role
  createRole: async (roleData: CreateRoleRequest): Promise<Role> => {
    try {
      const response = await fetch(`${API_URL}/roles/feature-roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify(roleData)
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to create role:', error);
      throw error;
    }
  },

  // Update a feature role
  updateRole: async (roleId: string, updates: UpdateRoleRequest): Promise<Role> => {
    try {
      const response = await fetch(`${API_URL}/roles/feature-roles/${roleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to update role:', error);
      throw error;
    }
  },

  // Delete a feature role
  deleteRole: async (roleId: string): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/roles/feature-roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to delete role:', error);
      throw error;
    }
  },

  // Get users with roles for organization
  getOrganizationUsersWithRoles: async (): Promise<any[]> => {
    try {
      const response = await fetch(`${API_URL}/roles/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch users with roles:', error);
      throw error;
    }
  },

  // Assign feature role to user
  assignUserRole: async (userId: number, roleId: string, hierarchyNodeId?: number, locationId?: number): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/roles/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify({
          userId,
          roleId,
          hierarchyNodeId,
          locationId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to assign role to user:', error);
      throw error;
    }
  },

  // Feature flag methods for role_feature_flags table
  getRoleFeatures: async (roleId: string): Promise<FeatureWithAssignment[]> => {
    try {
      const response = await fetch(`${API_URL}/roles/feature-roles/${roleId}/features`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch role features:', error);
      return [];
    }
  },

  updateRoleFeature: async (roleId: string, featureUpdate: UpdateFeatureFlagRequest): Promise<FeatureFlag> => {
    try {
      const response = await fetch(`${API_URL}/roles/feature-roles/${roleId}/features`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify(featureUpdate)
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to update role feature:', error);
      throw error;
    }
  }
};

export default RoleManagementService;